import { User, UserCreatePayload, UserUpdatePayload } from '@ekkogmbh/apisdk';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { PersonOutline, SmartToyOutlined } from '@mui/icons-material';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { CenteredColumn, ReactivePanelColumn, ReactiveRow } from 'src/Common/Components/Stacks';

interface Props {
  user?: User;
  closeHandler: () => void;
  saveHandler: (user: UserUpdatePayload | UserCreatePayload, password?: string) => Promise<void>;
  passwordEditable?: boolean;
}

export const UserPanel = ({ user, closeHandler, saveHandler, passwordEditable }: Props): React.JSX.Element => {
  const { enableSave } = usePanel();
  const [username, setUsername] = useState<string>(user?.username ?? '');
  const [coordinate, setCoordinate] = useState<string>(user?.coordinate ?? '');
  const [email, setEmail] = useState<string>(user?.email ?? '');
  const [isAutomation, setAutomation] = useState<boolean>(!!user?.automation);
  const [validUntil, setValidUntil] = useState<string>(
    (user?.validUntil ? moment(user.validUntil) : moment().add(1, 'd')).format('YYYY-MM-DD').toString(),
  );
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');

  // Track which fields have been touched
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const onReset = useCallback(() => {
    setUsername(user?.username ?? '');
    setCoordinate(user?.coordinate ?? '');
    setEmail(user?.email ?? '');
    setAutomation(!!user?.automation);
    setValidUntil((user?.validUntil ? moment(user.validUntil) : moment().add(1, 'd')).format('YYYY-MM-DD').toString());
    setPassword('');
    setTouched({});
  }, [user]);

  /** on user changes */
  useEffect(() => {
    onReset();
  }, [user]);

  const isPasswordOk = useMemo(() => {
    if (password === passwordConfirm) {
      return passwordEditable ? password !== '' : password === '';
    }

    return false;
  }, [passwordEditable, password, passwordConfirm]);

  useEffect(() => {
    enableSave(![username, coordinate, email].includes('') && isPasswordOk);
  }, [email, username, coordinate, isPasswordOk]);

  const onSave = useCallback(async () => {
    let payload: UserUpdatePayload | UserCreatePayload;
    if (user !== undefined) {
      payload = {
        id: user.id,
        email,
        validUntil,
        coordinate,
      };
    } else {
      payload = {
        username,
        email,
        validUntil: isAutomation ? undefined : validUntil,
        coordinate,
        automation: isAutomation,
      };
    }

    await saveHandler(payload, password !== '' ? password : undefined);
  }, [saveHandler, user, isAutomation, email, validUntil, coordinate, password]);

  return (
    <ContentPanel
      steps={[
        {
          title: 'User',
          contentCallback: () => (
            <ReactivePanelColumn>
              <StyledFormHeader label="Account Details" />
              <ReactiveRow>
                <StyledTextField
                  label={'Username *'}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={() => touch('username')}
                  disabled={user !== undefined}
                  error={touched.username && username === ''}
                  action={{
                    disabled: user !== undefined,
                    icon: isAutomation ? SmartToyOutlined : PersonOutline,
                    onClick: () => setAutomation(!isAutomation),
                    tooltip: isAutomation ? 'automation-user' : 'user',
                  }}
                />
                <StyledTextField
                  label={'E-Mail *'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => touch('email')}
                  error={touched.email && email === ''}
                />
              </ReactiveRow>
              <CoordinateInput trailingDelimiter={false} value={coordinate} onChange={(c) => setCoordinate(c)} />

              {passwordEditable && (
                <CenteredColumn width={'100%'}>
                  <StyledFormHeader label="Security" />
                  <StyledTextField
                    type={'password'}
                    label={'Password *'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => touch('password')}
                    variant={'outlined'}
                    error={touched.password && password === ''}
                  />
                  <StyledTextField
                    type={'password'}
                    label={'Password-Confirm *'}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    onBlur={() => touch('passwordConfirm')}
                    error={touched.passwordConfirm && (password !== passwordConfirm || passwordConfirm === '')}
                    helperText={
                      touched.passwordConfirm && password !== passwordConfirm && passwordConfirm !== ''
                        ? 'Passwords do not match'
                        : undefined
                    }
                  />
                </CenteredColumn>
              )}

              {!isAutomation && (
                <CenteredColumn width={'100%'}>
                  <StyledFormHeader label="Validity" />
                  <DatePicker
                    sx={{ width: '100%' }}
                    disablePast
                    label="Valid-Until"
                    value={moment(validUntil)}
                    onChange={(date: Moment | null) => {
                      if (date === null) {
                        return;
                      }
                      setValidUntil(date.format('YYYY-MM-DD'));
                    }}
                    minDate={moment().add(1, 'd')}
                  />
                </CenteredColumn>
              )}
            </ReactivePanelColumn>
          ),
        },
      ]}
      onCancel={() => {
        onReset();
        closeHandler();
      }}
      onReset={onReset}
      onSave={onSave}
    />
  );
};
